export default {
  zh: {
    hero: {
      title: "AI 大模型推理",
      titleHighlight: "一体化解决方案",
      description: "统一接入、智能路由、成本归因、工程交付——\n覆盖企业 AI 推理全生命周期",
      ctaButton: "立即咨询",
    },
    section1: {
      title: "统一接入与生产级推理保障",
      card1: {
        title: "统一接入与租户治理",
        subtitle: "一套接口纳管多云商模型推理服务",
        description:
          "以企业可用的统一调用面（含常见 OpenAI-compatible 适配）对接多家服务商，在同一控制台完成密钥分级、租户隔离与调用配额，缩短研发重复适配与多套计费对齐成本。",
        features: {
          f1: "多供应商统一接入与安全凭据轮换机制",
          f2: "项目/环境与 API Key 粒度管控",
          f3: "默认预算阈值与告警，前置防控异常用量",
        },
      },
      card2: {
        title: "策略路由与高可用推理",
        subtitle: "在约定 SLA 下平衡延迟、成功率与使用效果",
        description:
          "对齐「质量与稳定性不确定、单供应商限流／故障易受牵连」等企业关切，可由平台依据任务优先级、延迟阈值与容错策略在多路推理资源之间进行服务端路由与编排。",
        features: {
          f1: "按任务类型预设模型能力与延迟阶梯",
          f2: "限流／超时／错误率超限时的备选路径与健康检查",
          f3: "生产环境链路观测与 SLA 异常留痕便于复盘",
        },
      },
    },
    section2: {
      title: "对内成本归因与智算工程化交付",
      card3: {
        title: "对内计量结算与归因",
        subtitle: "按应用与用户维度厘清推理用量成本",
        description:
          "满足财务与业务部门按项目核算的需求：用量与资费数据归集在企业自有账单体系内呈现，并按应用、团队、产品线、调用任务等维度可追溯。",
        features: {
          f1: "多维标签下的用量分摊与对内报表导出",
          f2: "与内部成本中心／预算科目映射的工作流适配",
        },
      },
      card4: {
        title: "智算基建与行业落地",
        subtitle: "训推一体的算力与安全基线，联动业务系统集成",
        description:
          "在安全与合规基线之上，为企业提供 GPU／集群容量规划及网络与安全加固，辅以模型选型、微调与离线评测；通过标准化 API、消息流或中间件适配，将推理能力并入现有客服、研发、风控等业务流程。",
        features: {
          f1: "训推峰值与容量的规划与安全基线设计",
          f2: "评测集与离线回归，把控上线前后的质量阈值",
          f3: "行业场景 PoC→生产的一体化交付与试运行保障",
        },
      },
    },
  },
  en: {
    hero: {
      title: "Enterprise AI Inference",
      titleHighlight: "Integrated Solutions",
      description:
        "Unified access, intelligent routing, cost attribution, engineering delivery—\ncovering the full lifecycle of enterprise AI inference.",
      ctaButton: "Contact Us",
    },
    section1: {
      title: "Unified Access & Production-Grade Inference Assurance",
      card1: {
        title: "Unified Access & Tenant Governance",
        subtitle: "One interface to manage multi-vendor model inference",
        description:
          "Connect multiple providers via a unified enterprise-grade API layer (including OpenAI-compatible adapters). Manage API keys, tenant isolation, and call quotas in a single console, reducing repeated integration and billing alignment costs.",
        features: {
          f1: "Multi-vendor unified access & secure credential rotation",
          f2: "Project/environment & API Key-level access control",
          f3: "Default budget thresholds & alerts for proactive anomaly prevention",
        },
      },
      card2: {
        title: "Policy Routing & High-Availability Inference",
        subtitle: "Balance latency, success rate, and quality under agreed SLAs",
        description:
          "Address enterprise concerns around quality uncertainty and single-vendor rate limiting by enabling server-side routing and orchestration across multiple inference resources based on task priority, latency thresholds, and fault-tolerance policies.",
        features: {
          f1: "Pre-configured model capabilities and latency tiers by task type",
          f2: "Fallback paths & health checks for rate-limit/timeout/error-rate breaches",
          f3: "Production link observability with SLA anomaly tracing for post-mortem analysis",
        },
      },
    },
    section2: {
      title: "Internal Cost Attribution & AI Engineering Delivery",
      card3: {
        title: "Internal Metering, Settlement & Attribution",
        subtitle: "Clarify inference costs by application and user dimensions",
        description:
          "Meet finance and business teams' project-level accounting needs. Usage and billing data is surfaced within the enterprise's own billing system, traceable by application, team, product line, and task.",
        features: {
          f1: "Multi-tag usage allocation & internal report export",
          f2: "Workflow adapters mapping to internal cost centers / budget accounts",
        },
      },
      card4: {
        title: "AI Infrastructure & Industry Deployment",
        subtitle: "Compute & security baseline for training + inference integrated with business systems",
        description:
          "On top of security and compliance baselines, provide GPU/cluster capacity planning, network and security hardening, plus model selection, fine-tuning, and offline evaluation. Integrate inference capabilities into existing customer service, R&D, and risk workflows via standard APIs or middleware adapters.",
        features: {
          f1: "Training/inference peak capacity planning & security baseline design",
          f2: "Evaluation sets & offline regression to control pre/post-launch quality thresholds",
          f3: "Industry PoC→production integrated delivery with trial-run assurance",
        },
      },
    },
  },
  ja: {
    hero: {
      title: "エンタープライズ AI 推論",
      titleHighlight: "統合ソリューション",
      description:
        "統合アクセス、インテリジェントルーティング、コスト帰属、エンジニアリング納品——\n企業AIの全ライフサイクルをカバー。",
      ctaButton: "お問い合わせ",
    },
    section1: {
      title: "統合アクセスと本番グレード推論保証",
      card1: {
        title: "統合アクセスとテナント管理",
        subtitle: "1つのインターフェースでマルチベンダーモデル推論を統括",
        description:
          "企業向け統合APIレイヤー（OpenAI互換アダプター含む）で複数プロバイダーに接続。APIキー管理、テナント分離、呼び出しクォータを1つのコンソールで管理し、繰り返しの統合と請求調整のコストを削減します。",
        features: {
          f1: "マルチベンダー統合アクセスと安全な認証情報ローテーション",
          f2: "プロジェクト/環境とAPIキー粒度のアクセス制御",
          f3: "デフォルト予算閾値とアラートによる異常使用の事前防止",
        },
      },
      card2: {
        title: "ポリシールーティングと高可用性推論",
        subtitle: "合意したSLAの下でレイテンシ・成功率・品質のバランスを実現",
        description:
          "品質・安定性の不確実性や単一ベンダーのレート制限に関する企業の懸念に対応。タスク優先度、レイテンシ閾値、フォールトトレランスポリシーに基づき複数の推論リソース間でサーバー側ルーティングとオーケストレーションを実現します。",
        features: {
          f1: "タスクタイプ別のモデル能力とレイテンシ階層のプリセット",
          f2: "レート制限/タイムアウト/エラー率超過時のフォールバックパスとヘルスチェック",
          f3: "本番環境のリンク観測とSLA異常記録による振り返り支援",
        },
      },
    },
    section2: {
      title: "内部コスト帰属とAIエンジニアリング納品",
      card3: {
        title: "内部計量・決済・帰属",
        subtitle: "アプリケーションとユーザー次元で推論使用コストを明確化",
        description:
          "財務・事業部門のプロジェクト単位会計ニーズを満たします。使用量と課金データは企業独自の請求システム内に集約表示され、アプリケーション、チーム、製品ライン、タスクなどの次元で追跡可能です。",
        features: {
          f1: "多次元タグによる使用量配分と社内レポートエクスポート",
          f2: "内部コストセンター/予算科目へのマッピングワークフロー適合",
        },
      },
      card4: {
        title: "AIインフラと業界展開",
        subtitle: "学習・推論一体の算力と安全基準、業務システム統合",
        description:
          "セキュリティ・コンプライアンス基準の上に、GPU/クラスター容量計画とネットワーク・セキュリティ強化を提供。モデル選択、ファインチューニング、オフライン評価を補助し、標準APIやミドルウェアアダプターで推論能力を既存の業務フローに統合します。",
        features: {
          f1: "学習・推論ピーク容量計画とセキュリティ基準設計",
          f2: "品質閾値を管理する評価セットとオフライン回帰テスト",
          f3: "業界シナリオPoCから本番への一体型納品と試運転保証",
        },
      },
    },
  },
};
